/**
 * useClientOverlapReport — Mock data, computed aggregates, and export triggers
 * for the Client Overlap Report page.
 *
 * Maps to: OverlapFlag, DeficiencyRecord, Claimant types from claimant.types.ts
 * Status display mapping:
 *   CONFIRMED      -> "ACCEPTED"
 *   PENDING_REVIEW -> "PENDING"
 *   REJECTED       -> "REJECTED"
 */
import { useMemo, useCallback } from 'react';
import type {
  Claimant, OverlapFlag, DeficiencyRecord, OverlapStatus, ConfidenceScore
} from '@/types/cmm/claimant.types';
import { downloadMultiSheetExcel, downloadCSV } from '@/utils/excelExport';
import type { SheetDefinition } from '@/utils/excelExport';

// ── Display helpers ──────────────────────────────────────────
export const STATUS_DISPLAY: Record<OverlapStatus, string> = {
  CONFIRMED: 'ACCEPTED',
  PENDING_REVIEW: 'PENDING',
  REJECTED: 'REJECTED',
};

// ── Extended report row (joins claimant + overlap + deficiencies) ──
export interface ReportOverlapRow {
  claimantId: string;
  claimantName: string;
  addressValidated: string;
  state: string;
  litigationName: string;
  matchType: string;
  confidenceScore: ConfidenceScore;
  ruleApplied: string;
  estClaimValue: number;
  overlapStatus: OverlapStatus;
  evidenceOnFile: string[];
  evidenceGaps: string[];
  deficiencies: DeficiencyRecord[];
}

export interface LitigationBreakdown {
  litigationName: string;
  totalOverlaps: number;
  completedOverlaps: number;   // CONFIRMED + zero open deficiencies
  deficientOverlaps: number;   // (CONFIRMED or PENDING_REVIEW) + at least 1 open deficiency
  rulesApplied: string[];      // distinct ruleApplied codes
  rows: ReportOverlapRow[];
  narrativeSummary: string;
}

export interface PortfolioSummary {
  totalUnmonetizedValue: number;
  totalClaimantsAnalyzed: number;
  totalWithOverlaps: number;
}

// ── Mock claimants (extends existing VGA mock with RLP + PFAS) ──
const MOCK_CLAIMANTS: Claimant[] = [
  {
    id: 'CLM-001', tortCode: 'RLP', sourceFirmId: 'paluch-llc',
    firstName: 'Jessica', lastName: 'Rupe', dob: '1988-03-15', ssnLast4: '4821',
    email: 'jessica.rupe@placeholder.dev', phonePrimary: '+13155550101',
    addressRaw: '412 Maple Ave, Cortland, NY 13045',
    addressValidated: { deliveryLine1: '412 Maple Ave', lastLine: 'Cortland NY 13045', city: 'Cortland', state: 'NY', zip5: '13045', dpvMatchCode: 'Y' },
    residencyStartDate: '2019-01-01', state: 'NY', pipelineStage: 'SUBSTANTIATED',
    estPortfolioValueRange: '$45K-$90K', createdAt: '2024-11-22T16:36:00Z', updatedAt: '2024-11-22T16:36:00Z',
  },
  {
    id: 'CLM-002', tortCode: 'RLP', sourceFirmId: 'paluch-llc',
    firstName: 'Joshua', lastName: 'Cork', dob: '1992-06-20', ssnLast4: '7734',
    email: 'joshua.cork@placeholder.dev', phonePrimary: '+15095550188',
    addressRaw: '881 Pine St, Spokane, WA 99207',
    addressValidated: { deliveryLine1: '881 Pine St', lastLine: 'Spokane WA 99207', city: 'Spokane', state: 'WA', zip5: '99207', dpvMatchCode: 'Y' },
    residencyStartDate: '2020-03-01', state: 'WA', pipelineStage: 'DOC_COLLECTION',
    estPortfolioValueRange: '$30K-$60K', createdAt: '2024-12-01T10:00:00Z', updatedAt: '2024-12-01T10:00:00Z',
  },
  {
    id: 'CLM-003', tortCode: 'RLP', sourceFirmId: 'paluch-llc',
    firstName: 'Maria', lastName: 'Santos', dob: '1975-09-12', ssnLast4: '3391',
    email: 'maria.santos@placeholder.dev', phonePrimary: '+12125550199',
    addressRaw: '220 W 42nd St, New York, NY 10036',
    addressValidated: { deliveryLine1: '220 W 42nd St', lastLine: 'New York NY 10036', city: 'New York', state: 'NY', zip5: '10036', dpvMatchCode: 'Y' },
    residencyStartDate: '2018-06-01', state: 'NY', pipelineStage: 'SUBSTANTIATED',
    estPortfolioValueRange: '$55K-$110K', createdAt: '2024-10-15T09:00:00Z', updatedAt: '2024-10-15T09:00:00Z',
  },
  {
    id: 'CLM-004', tortCode: 'PFAS', sourceFirmId: 'paluch-llc',
    firstName: 'Robert', lastName: 'Chen', dob: '1965-01-30', ssnLast4: '5582',
    email: 'robert.chen@placeholder.dev', phonePrimary: '+17135550144',
    addressRaw: '1400 Fannin St, Houston, TX 77002',
    addressValidated: { deliveryLine1: '1400 Fannin St', lastLine: 'Houston TX 77002', city: 'Houston', state: 'TX', zip5: '77002', dpvMatchCode: 'Y' },
    residencyStartDate: '2010-01-01', state: 'TX', pipelineStage: 'REVIEW',
    estPortfolioValueRange: '$80K-$150K', createdAt: '2025-01-05T14:00:00Z', updatedAt: '2025-01-05T14:00:00Z',
  },
  {
    id: 'CLM-005', tortCode: 'PFAS', sourceFirmId: 'paluch-llc',
    firstName: 'Angela', lastName: 'Whitmore', dob: '1970-11-08', ssnLast4: '6217',
    email: 'angela.whitmore@placeholder.dev', phonePrimary: '+16145550133',
    addressRaw: '55 Public Square, Cleveland, OH 44113',
    addressValidated: { deliveryLine1: '55 Public Square', lastLine: 'Cleveland OH 44113', city: 'Cleveland', state: 'OH', zip5: '44113', dpvMatchCode: 'D' },
    residencyStartDate: '2012-04-01', state: 'OH', pipelineStage: 'DOC_COLLECTION',
    estPortfolioValueRange: '$60K-$120K', createdAt: '2025-01-10T08:00:00Z', updatedAt: '2025-01-10T08:00:00Z',
  },
  {
    id: 'CLM-006', tortCode: 'RLP', sourceFirmId: 'paluch-llc',
    firstName: 'David', lastName: 'Okafor', dob: '1990-04-22', ssnLast4: '8843',
    email: 'david.okafor@placeholder.dev', phonePrimary: '+14045550177',
    addressRaw: '200 Peachtree St, Atlanta, GA 30303',
    addressValidated: { deliveryLine1: '200 Peachtree St', lastLine: 'Atlanta GA 30303', city: 'Atlanta', state: 'GA', zip5: '30303', dpvMatchCode: 'Y' },
    residencyStartDate: '2021-02-01', state: 'GA', pipelineStage: 'SUBSTANTIATED',
    estPortfolioValueRange: '$35K-$70K', createdAt: '2025-02-01T11:00:00Z', updatedAt: '2025-02-01T11:00:00Z',
  },
];

const MOCK_OVERLAPS: (OverlapFlag & { estClaimValue: number })[] = [
  // RLP — RealPage Rental Litigation
  {
    id: 'OVL-001', claimantId: 'CLM-001', litigationName: 'RealPage Rental Pricing (RLP)',
    matchType: 'geographic', status: 'CONFIRMED', confidenceScore: 'confirmed',
    ruleApplied: 'RULE-001', evidenceOnFile: ['Lease Agreement', 'Proof of Residency'],
    evidenceGaps: [], createdAt: '2025-01-15T10:00:00Z', estClaimValue: 67500,
  },
  {
    id: 'OVL-002', claimantId: 'CLM-002', litigationName: 'RealPage Rental Pricing (RLP)',
    matchType: 'geographic', status: 'PENDING_REVIEW', confidenceScore: 'probable',
    ruleApplied: 'RULE-001', evidenceOnFile: ['Lease Agreement'],
    evidenceGaps: ['Rent Payment History'], createdAt: '2025-01-18T14:00:00Z', estClaimValue: 45000,
  },
  {
    id: 'OVL-003', claimantId: 'CLM-003', litigationName: 'RealPage Rental Pricing (RLP)',
    matchType: 'geographic', status: 'CONFIRMED', confidenceScore: 'confirmed',
    ruleApplied: 'RULE-003', evidenceOnFile: ['Lease Agreement', 'Rent Payment History', 'Proof of Residency'],
    evidenceGaps: [], createdAt: '2025-01-20T09:00:00Z', estClaimValue: 82500,
  },
  {
    id: 'OVL-004', claimantId: 'CLM-006', litigationName: 'RealPage Rental Pricing (RLP)',
    matchType: 'behavioral', status: 'CONFIRMED', confidenceScore: 'confirmed',
    ruleApplied: 'TRIG-003', evidenceOnFile: ['Lease Agreement'],
    evidenceGaps: ['Proof of Residency'], createdAt: '2025-02-05T10:00:00Z', estClaimValue: 52500,
  },
  // PFAS — Water Contamination
  {
    id: 'OVL-005', claimantId: 'CLM-004', litigationName: 'PFAS Water Contamination',
    matchType: 'geographic', status: 'CONFIRMED', confidenceScore: 'confirmed',
    ruleApplied: 'RULE-007', evidenceOnFile: ['Proof of Exposure', 'Employment Service Record', 'Diagnosis Documentation'],
    evidenceGaps: [], createdAt: '2025-01-25T12:00:00Z', estClaimValue: 115000,
  },
  {
    id: 'OVL-006', claimantId: 'CLM-005', litigationName: 'PFAS Water Contamination',
    matchType: 'diagnosis', status: 'PENDING_REVIEW', confidenceScore: 'probable',
    ruleApplied: 'RULE-007', evidenceOnFile: ['Proof of Exposure'],
    evidenceGaps: ['Diagnosis Documentation', 'Employment Service Record'], createdAt: '2025-02-01T08:00:00Z', estClaimValue: 90000,
  },
  // Rejected overlap (excluded from value calculations)
  {
    id: 'OVL-007', claimantId: 'CLM-001', litigationName: 'PFAS Water Contamination',
    matchType: 'geographic', status: 'REJECTED', confidenceScore: 'unverified',
    ruleApplied: 'RULE-007', evidenceOnFile: [],
    evidenceGaps: ['Proof of Exposure', 'Diagnosis Documentation'], createdAt: '2025-02-10T10:00:00Z', estClaimValue: 0,
  },
];

const MOCK_DEFICIENCIES: DeficiencyRecord[] = [
  {
    id: 'DEF-001', claimantId: 'CLM-002',
    deficiencyCode: 'DEF_MISSING_REQUIRED_DOC',
    message: 'Rent Payment History is required to substantiate RLP claim damages calculation.',
    raisedAt: '2025-01-20T10:00:00Z',
  },
  {
    id: 'DEF-002', claimantId: 'CLM-006',
    deficiencyCode: 'DEF_MISSING_REQUIRED_DOC',
    message: 'Proof of Residency is required to confirm geographic overlap for Atlanta property.',
    raisedAt: '2025-02-06T09:00:00Z',
  },
  {
    id: 'DEF-003', claimantId: 'CLM-005',
    deficiencyCode: 'DEF_MISSING_REQUIRED_DOC',
    message: 'Diagnosis Documentation is required for PFAS health impact verification.',
    raisedAt: '2025-02-02T11:00:00Z',
  },
  {
    id: 'DEF-004', claimantId: 'CLM-005',
    deficiencyCode: 'DEF_MISSING_REQUIRED_DOC',
    message: 'Employment Service Record needed to confirm occupational exposure pathway.',
    raisedAt: '2025-02-02T11:05:00Z',
  },
  // Cleared deficiency
  {
    id: 'DEF-005', claimantId: 'CLM-003',
    deficiencyCode: 'DEF_ADDRESS_MISMATCH',
    message: 'Validated address did not match original lease. Re-verified via Smarty — now resolved.',
    raisedAt: '2025-01-18T08:00:00Z',
    clearedAt: '2025-01-19T14:00:00Z',
    clearedByUserId: 'admin-001',
  },
];

// ── Narrative generator ──────────────────────────────────────
function generateNarrative(lit: string, rules: string[], totalOverlaps: number, completedOverlaps: number): string {
  const ruleList = rules.map(r => '`' + r + '`').join(', ');
  const litShort = lit.replace(/\s*\(.*\)/, '');
  if (completedOverlaps === totalOverlaps) {
    return 'ClaimsIQ identified ' + totalOverlaps + ' overlap' + (totalOverlaps > 1 ? 's' : '') + ' across the ' + litShort + ' litigation. '
      + 'All claims were confirmed using matching criteria ' + ruleList + '. '
      + 'Every overlap has been fully substantiated with no outstanding deficiencies.';
  }
  const pending = totalOverlaps - completedOverlaps;
  return 'ClaimsIQ identified ' + totalOverlaps + ' overlap' + (totalOverlaps > 1 ? 's' : '') + ' across the ' + litShort + ' litigation using matching criteria ' + ruleList + '. '
    + completedOverlaps + ' overlap' + (completedOverlaps !== 1 ? 's have' : ' has') + ' been fully substantiated. '
    + pending + ' claim' + (pending !== 1 ? 's' : '') + ' still require' + (pending === 1 ? 's' : '') + ' additional documentation before they can be finalized.';
}

// ── Legend / methodology static data ─────────────────────────
export const MATCH_TYPE_LEGEND: { code: string; meaning: string }[] = [
  { code: 'geographic', meaning: 'Claimant address falls within a confirmed affected geographic zone (property match, contamination radius, or approved site list).' },
  { code: 'behavioral', meaning: 'Claimant activity patterns (rental history, usage data) match expected litigation criteria.' },
  { code: 'diagnosis', meaning: 'Claimant medical diagnosis aligns with conditions associated with the litigation exposure.' },
];

export const CONFIDENCE_LEGEND: { tier: string; meaning: string }[] = [
  { tier: 'confirmed', meaning: 'High confidence — DPV match code Y, all required evidence on file, rule criteria fully satisfied.' },
  { tier: 'probable', meaning: 'Moderate confidence — DPV match code D or S, some evidence present but gaps remain.' },
  { tier: 'unverified', meaning: 'Low confidence — DPV match code N or insufficient evidence. Requires manual review.' },
];

export const RULE_LEGEND: { code: string; criteria: string }[] = [
  { code: 'RULE-001', criteria: 'Geographic proximity match — claimant validated address falls within a RealPage-managed property zone during the class period.' },
  { code: 'RULE-003', criteria: 'Full documentation match — geographic overlap plus complete rent payment history confirming sustained residency and pricing impact.' },
  { code: 'RULE-007', criteria: 'PFAS exposure pathway match — claimant address within EPA-designated contamination zone or occupational exposure confirmed via employment records.' },
  { code: 'TRIG-003', criteria: 'Behavioral pattern trigger — rental pricing anomaly detected via algorithmic comparison with regional market data.' },
];

export const DEFICIENCY_CODE_LEGEND: { code: string; verification: string }[] = [
  { code: 'DEF_MISSING_REQUIRED_DOC', verification: 'One or more required documents have not been uploaded. Client must provide the specified document(s) listed in the deficiency message.' },
  { code: 'DEF_ADDRESS_MISMATCH', verification: 'Validated address does not match the address on supporting documentation. Client must provide corrected documents or re-verify via Smarty.' },
  { code: 'DEF_CROSS_TORT_UNACKNOWLEDGED', verification: 'Claimant appears in multiple tort pipelines. Firm admin must acknowledge cross-litigation status before packet generation.' },
  { code: 'DEF_OVERLAP_PENDING', verification: 'Overlap has been in PENDING status for more than 5 business days. Human review is required to accept or reject.' },
];

export const STATUS_LEGEND: { status: string; definition: string }[] = [
  { status: 'PENDING (PENDING_REVIEW)', definition: 'Overlap has been flagged by the engine but has not yet been reviewed by a human reviewer. Awaiting accept/reject decision.' },
  { status: 'ACCEPTED (CONFIRMED)', definition: 'Overlap has been confirmed and accepted. If all deficiencies are cleared, the claim is ready for packet generation.' },
  { status: 'REJECTED', definition: 'Overlap was reviewed and determined to be a false positive. Excluded from portfolio value calculations.' },
];

// ── Main hook ────────────────────────────────────────────────
export function useClientOverlapReport() {
  // Build joined report rows
  const reportRows = useMemo<ReportOverlapRow[]>(() => {
    return MOCK_OVERLAPS.map(ovl => {
      const claimant = MOCK_CLAIMANTS.find(c => c.id === ovl.claimantId)!;
      const defs = MOCK_DEFICIENCIES.filter(d => d.claimantId === ovl.claimantId);
      const addr = claimant.addressValidated
        ? claimant.addressValidated.deliveryLine1 + ', ' + claimant.addressValidated.city + ', ' + claimant.addressValidated.state + ' ' + claimant.addressValidated.zip5
        : claimant.addressRaw;
      return {
        claimantId: claimant.id,
        claimantName: claimant.firstName + ' ' + claimant.lastName,
        addressValidated: addr,
        state: claimant.state,
        litigationName: ovl.litigationName,
        matchType: ovl.matchType,
        confidenceScore: ovl.confidenceScore,
        ruleApplied: ovl.ruleApplied,
        estClaimValue: ovl.estClaimValue,
        overlapStatus: ovl.status,
        evidenceOnFile: ovl.evidenceOnFile,
        evidenceGaps: ovl.evidenceGaps,
        deficiencies: defs,
      };
    });
  }, []);

  // Portfolio summary
  const portfolioSummary = useMemo<PortfolioSummary>(() => {
    const nonRejected = reportRows.filter(r => r.overlapStatus !== 'REJECTED');
    const claimantIdsWithOverlaps = new Set(nonRejected.map(r => r.claimantId));
    return {
      totalUnmonetizedValue: nonRejected.reduce((sum, r) => sum + r.estClaimValue, 0),
      totalClaimantsAnalyzed: MOCK_CLAIMANTS.length,
      totalWithOverlaps: claimantIdsWithOverlaps.size,
    };
  }, [reportRows]);

  // Per-litigation breakdown
  const litigationBreakdowns = useMemo<LitigationBreakdown[]>(() => {
    const litMap = new Map<string, ReportOverlapRow[]>();
    for (const row of reportRows) {
      if (row.overlapStatus === 'REJECTED') continue;
      const existing = litMap.get(row.litigationName) ?? [];
      existing.push(row);
      litMap.set(row.litigationName, existing);
    }

    return Array.from(litMap.entries()).map(([litName, rows]) => {
      const totalOverlaps = rows.length;
      const completedOverlaps = rows.filter(r =>
        r.overlapStatus === 'CONFIRMED' &&
        r.deficiencies.filter(d => !d.clearedAt).length === 0
      ).length;
      const deficientOverlaps = rows.filter(r =>
        (r.overlapStatus === 'CONFIRMED' || r.overlapStatus === 'PENDING_REVIEW') &&
        r.deficiencies.filter(d => !d.clearedAt).length > 0
      ).length;
      const rulesApplied = Array.from(new Set(rows.map(r => r.ruleApplied)));
      const narrative = generateNarrative(litName, rulesApplied, totalOverlaps, completedOverlaps);

      return { litigationName: litName, totalOverlaps, completedOverlaps, deficientOverlaps, rulesApplied, rows, narrativeSummary: narrative };
    });
  }, [reportRows]);

  // ── Export: multi-sheet Excel ──────────────────────────────
  const exportExcel = useCallback(() => {
    const nonRejectedRows = reportRows.filter(r => r.overlapStatus !== 'REJECTED');

    const sheet1: SheetDefinition = {
      name: 'Claims Detail',
      headers: [
        'ClaimsIQ ID', 'Claimant Name', 'Address (Validated)', 'State',
        'Litigation Name', 'Match Type', 'Confidence Score', 'Rule Applied',
        'Estimated Claim Value', 'Overlap Status', 'Evidence On File',
        'Evidence Gaps', 'Deficiency Codes', 'Deficiency Messages'
      ],
      rows: nonRejectedRows.map(r => [
        r.claimantId, r.claimantName, r.addressValidated, r.state,
        r.litigationName, r.matchType, r.confidenceScore, r.ruleApplied,
        r.estClaimValue, STATUS_DISPLAY[r.overlapStatus],
        r.evidenceOnFile.join(', '), r.evidenceGaps.join(', '),
        r.deficiencies.map(d => d.deficiencyCode).join(', '),
        r.deficiencies.map(d => d.message).join('; '),
      ]),
    };

    const allDefs = nonRejectedRows.flatMap(r =>
      r.deficiencies.map(d => ({
        ...d,
        reportClaimantId: r.claimantId,
        reportClaimantName: r.claimantName,
        reportLitigationName: r.litigationName,
      }))
    );

    const sheet2: SheetDefinition = {
      name: 'Deficiency Requests',
      headers: [
        'ClaimsIQ ID', 'Claimant Name', 'Litigation Name',
        'Deficiency Code', 'Deficiency Message', 'Raised Date', 'Status'
      ],
      rows: allDefs.map(d => [
        d.reportClaimantId, d.reportClaimantName, d.reportLitigationName,
        d.deficiencyCode, d.message,
        new Date(d.raisedAt).toLocaleDateString(),
        d.clearedAt ? 'Cleared' : 'Open',
      ]),
    };

    const sheet3: SheetDefinition = {
      name: 'Legend & Methodology',
      headers: ['Category', 'Code / Tier', 'Description'],
      rows: [
        ['--- Match Types ---', '', ''],
        ...MATCH_TYPE_LEGEND.map(m => ['Match Type', m.code, m.meaning]),
        ['', '', ''],
        ['--- Confidence Tiers ---', '', ''],
        ...CONFIDENCE_LEGEND.map(c => ['Confidence', c.tier, c.meaning]),
        ['', '', ''],
        ['--- Rule Codes ---', '', ''],
        ...RULE_LEGEND.map(r => ['Rule', r.code, r.criteria]),
        ['', '', ''],
        ['--- Deficiency Codes ---', '', ''],
        ...DEFICIENCY_CODE_LEGEND.map(d => ['Deficiency', d.code, d.verification]),
        ['', '', ''],
        ['--- Overlap Status ---', '', ''],
        ...STATUS_LEGEND.map(s => ['Status', s.status, s.definition]),
      ],
    };

    const dateStr = new Date().toISOString().slice(0, 10);
    downloadMultiSheetExcel([sheet1, sheet2, sheet3], 'ClaimsIQ_Client_Overlap_Report_' + dateStr + '.xlsx');
  }, [reportRows]);

  // ── Export: CSV (Sheet 1 only) ─────────────────────────────
  const exportCSV = useCallback(() => {
    const nonRejectedRows = reportRows.filter(r => r.overlapStatus !== 'REJECTED');
    const headers = [
      'ClaimsIQ ID', 'Claimant Name', 'Address (Validated)', 'State',
      'Litigation Name', 'Match Type', 'Confidence Score', 'Rule Applied',
      'Estimated Claim Value', 'Overlap Status', 'Evidence On File',
      'Evidence Gaps', 'Deficiency Codes', 'Deficiency Messages'
    ];
    const rows = nonRejectedRows.map(r => [
      r.claimantId, r.claimantName, r.addressValidated, r.state,
      r.litigationName, r.matchType, r.confidenceScore, r.ruleApplied,
      r.estClaimValue, STATUS_DISPLAY[r.overlapStatus],
      r.evidenceOnFile.join(', '), r.evidenceGaps.join(', '),
      r.deficiencies.map(d => d.deficiencyCode).join(', '),
      r.deficiencies.map(d => d.message).join('; '),
    ]);
    const dateStr = new Date().toISOString().slice(0, 10);
    downloadCSV(headers, rows, 'ClaimsIQ_Claims_Detail_' + dateStr + '.csv');
  }, [reportRows]);

  return {
    reportRows,
    portfolioSummary,
    litigationBreakdowns,
    exportExcel,
    exportCSV,
  };
}
